import { Box } from "@mui/material";
import { alpha, lighten, useTheme, type Theme } from "@mui/material/styles";
import { useEffect, useMemo, useRef, type JSX } from "react";

import { prepareLandMask, sampleLandDots, type LandDot } from "./dots.js";
import { latLngToVec, rotX, rotY, slerp, type Vec3 } from "./geo.js";
import { loadLandRings } from "./land.js";

/**
 * The sign-in hero: a dot globe in pure Canvas2D, no WebGL and no
 * dependencies.
 *
 * This is the customer support portal's `GlobePacketCanvas`
 * (`xop-edge-customer/apps/portal-web/src/components/`) rebuilt for this app,
 * and it is meant to read as the same object: a pale sphere, continents drawn
 * as a stipple of dots, a faint graticule, bright network nodes on the
 * surface, and packets that arc between them on great circles leaving a
 * ripple behind. The restraint is copied too — one slow rotation, nothing
 * that flashes, nothing that demands attention while someone types a
 * password.
 *
 * Two deliberate differences from the reference:
 *
 *  1. **Colour.** Every value comes from `theme.palette.accent` (teal) where
 *     the reference uses brand blue, so the two sign-ins are tellable apart
 *     at a glance. Lime survives on the network nodes and packets, because
 *     that is the XOP family signature the two portals should still share.
 *     Nothing here is a literal colour: re-skin `accent.ts` and the planet
 *     re-skins.
 *
 *  2. **Coastlines.** The reference approximates the world with ~25
 *     hand-typed polygons. We rasterise the vendored Natural Earth 110m land
 *     file instead (`dots.ts`), so the dots sit on real coastlines. Same
 *     design, better source data — and a genuine improvement rather than a
 *     divergence, because at dot resolution the *look* is identical; only the
 *     shapes are right.
 *
 * The nodes are illustrative, not data: this portal has no reseller-location
 * feed, and plotting one would be a lie about something we do not have. They
 * are major world cities, exactly as the reference's defaults are.
 */

/** Illustrative network nodes — major world cities, so the planet reads as
 * "a global network" from any rotation. Same set as the reference's
 * `DEFAULT_CUSTOMERS`. */
const NODES: ReadonlyArray<readonly [number, number]> = [
  [38.9, -77.04],
  [19.43, -99.13],
  [-15.79, -47.88],
  [-34.61, -58.38],
  [51.51, -0.13],
  [48.86, 2.35],
  [52.52, 13.4],
  [40.42, -3.7],
  [59.33, 18.07],
  [55.75, 37.62],
  [41.01, 28.98],
  [30.04, 31.23],
  [6.52, 3.38],
  [-1.29, 36.82],
  [-33.92, 18.42],
  [25.2, 55.27],
  [28.61, 77.21],
  [19.08, 72.88],
  [39.9, 116.4],
  [35.68, 139.65],
  [1.35, 103.82],
  [13.76, 100.5],
  [-6.2, 106.85],
  [-33.86, 151.21],
  [-36.85, 174.76],
];

/** Rotation speed, rad/ms. Roughly one turn every 5 minutes — present, but
 * slow enough that it never pulls the eye off the form. */
const YAW_VEL = 0.00018;
/** Camera tilt: the viewer sits a little above the equator. */
const PITCH = -0.15;
/** The intro spins faster and eases down, so the planet arrives rather than
 * simply being there. Matches the reference's 1.5s / 6× settle. */
const INTRO_MS = 1500;
const INTRO_MULT = 6;
/** Cap the backing store at 2× — beyond that a dot field costs fill rate
 * for detail nobody can resolve. */
const MAX_DPR = 2;

interface Palette {
  sphereInner: string;
  sphereMid: string;
  sphereOuter: string;
  sphereEdge: string;
  atmosphere: (a: number) => string;
  graticule: (a: number) => string;
  /** Continent dots, as an `rgba(r, g, b, ` prefix — the alpha varies per
   * dot with its depth, and building it by concatenation avoids a colour
   * parse per dot per frame. */
  dotFront: string;
  nodeCore: (a: number) => string;
  nodeHalo: (a: number) => string;
  ripple: (a: number) => string;
  packet: string;
  packetGlow: string;
}

/**
 * The planet's colours, all of them from the live theme.
 *
 * Like the reference, the globe stays *light* in both themes — it is the lit
 * face of a planet, and a dark one would vanish into the dark field behind
 * it. So the ramp is used at its pale end for the body and its deep end for
 * the ink, in both modes.
 */
export function buildPalette(theme: Theme): Palette {
  const { accent, secondary, common } = theme.palette;
  const rgba = (hex: string) => (a: number) => alpha(hex, a);
  return {
    sphereInner: common.white,
    // Washed well toward white rather than used at full strength. The
    // reference's body runs white -> a very pale tint -> a mid tint, and the
    // tint carries far less chroma than the accent does at 200/300: used raw,
    // the planet reads as a solid mint ball instead of a lit white sphere.
    sphereMid: lighten(accent[200], 0.72),
    sphereOuter: lighten(accent[300], 0.34),
    sphereEdge: alpha(accent[600], 0.65),
    atmosphere: rgba(accent[500]),
    graticule: rgba(accent[800]),
    dotFront: `${accent[800]}`,
    nodeCore: rgba(secondary.main),
    nodeHalo: rgba(secondary.main),
    ripple: rgba(secondary.main),
    packet: secondary.main,
    packetGlow: accent[400],
  };
}

interface Packet {
  from: number;
  to: number;
  t: number;
  speed: number;
  bursting: boolean;
}

interface Burst {
  vec: Vec3;
  age: number;
  ttl: number;
}

export function DotGlobeCanvas({ className }: { className?: string }): JSX.Element {
  const theme = useTheme();
  const palette = useMemo(() => buildPalette(theme), [theme]);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // happy-dom (tests) has no 2D context. There is nothing to degrade to and
    // nothing to warn about — the element is decorative.
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    const reduceMotion = Boolean(
      globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    );

    const dpr = Math.min(globalThis.devicePixelRatio || 1, MAX_DPR);
    let cssW = 0;
    let cssH = 0;
    let cx = 0;
    let cy = 0;
    let radius = 0;
    let disposed = false;
    let raf = 0;

    /* Continent dots. Empty until the coastline file lands (and permanently
       empty if it doesn't) — the sphere, graticule, nodes and packets are all
       drawn regardless, so a failed fetch costs the continents, not the
       hero. */
    let landVecs: Vec3[] = [];

    const nodeVecs: Vec3[] = NODES.map(([lat, lng]) => latLngToVec(lat, lng));
    const packets: Packet[] = [];
    const bursts: Burst[] = [];

    function spawnPacket(seeded = false) {
      if (nodeVecs.length < 2) return;
      let from = 0;
      let to = 0;
      while (from === to) {
        from = Math.floor(Math.random() * nodeVecs.length);
        to = Math.floor(Math.random() * nodeVecs.length);
      }
      packets.push({
        from,
        to,
        t: seeded ? Math.random() * 0.6 : 0,
        speed: 0.00005 + Math.random() * 0.00012,
        bursting: !seeded,
      });
      if (seeded) return;
      // Double ring — a tight one and a delayed wider one — so a launch reads
      // as an event rather than as a dot changing size.
      bursts.push({ vec: nodeVecs[from]!, age: 0, ttl: 1400 + Math.random() * 600 });
      bursts.push({ vec: nodeVecs[from]!, age: -180, ttl: 1900 + Math.random() * 500 });
    }
    // Seed several in flight so the planet is alive on the first frame rather
    // than filling up over the following ten seconds.
    for (let i = 0; i < 14; i += 1) spawnPacket(true);
    let nextSpawnIn = 250;

    let yaw = 0;
    const introStart = performance.now();
    let lastFrame = introStart;

    function resize(): void {
      cssW = canvas!.clientWidth;
      cssH = canvas!.clientHeight;
      canvas!.width = Math.max(1, Math.floor(cssW * dpr));
      canvas!.height = Math.max(1, Math.floor(cssH * dpr));
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      cx = cssW / 2;
      cy = cssH / 2;
      // 0.42, not 0.5 — the atmosphere and the node halos need room inside
      // the box or they clip against its edge.
      radius = Math.min(cssW, cssH) * 0.42;
    }

    function project(v: Vec3): { x: number; y: number; z: number } {
      const b = rotX(rotY(v, yaw), PITCH);
      return { x: cx + b[0] * radius, y: cy - b[1] * radius, z: b[2] };
    }

    function drawSphere(): void {
      // Body first, and opaque: the page's own dot field sits behind the
      // canvas, and letting it bleed through reads as noise *inside* the
      // planet.
      const grad = ctx!.createRadialGradient(
        cx - radius * 0.3,
        cy - radius * 0.32,
        radius * 0.1,
        cx,
        cy,
        radius,
      );
      grad.addColorStop(0, palette.sphereInner);
      grad.addColorStop(0.55, palette.sphereMid);
      grad.addColorStop(1, palette.sphereOuter);
      ctx!.fillStyle = grad;
      ctx!.beginPath();
      ctx!.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx!.fill();

      // Tight atmosphere — short reach, so it haloes the limb without
      // bleeding into the page behind it.
      const atmo = ctx!.createRadialGradient(cx, cy, radius * 0.99, cx, cy, radius * 1.12);
      atmo.addColorStop(0, palette.atmosphere(0.28));
      atmo.addColorStop(1, palette.atmosphere(0));
      ctx!.fillStyle = atmo;
      ctx!.beginPath();
      ctx!.arc(cx, cy, radius * 1.12, 0, Math.PI * 2);
      ctx!.fill();

      ctx!.strokeStyle = palette.sphereEdge;
      ctx!.lineWidth = 1.4;
      ctx!.beginPath();
      ctx!.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx!.stroke();
    }

    /** Graticule every 30°, drawn as short segments whose alpha follows depth
     * so the lines fade out as they turn away rather than stopping dead at
     * the limb. */
    function drawGraticule(): void {
      ctx!.lineWidth = 0.7;
      const arc = (points: Vec3[]) => {
        let prev: { x: number; y: number; z: number } | null = null;
        for (const v of points) {
          const p = project(v);
          if (prev && (p.z > -0.05 || prev.z > -0.05)) {
            const depth = Math.max(0, Math.min(1, (Math.min(p.z, prev.z) + 0.05) / 1.05));
            ctx!.strokeStyle = palette.graticule(0.14 * depth);
            ctx!.beginPath();
            ctx!.moveTo(prev.x, prev.y);
            ctx!.lineTo(p.x, p.y);
            ctx!.stroke();
          }
          prev = p;
        }
      };
      for (let lat = -60; lat <= 60; lat += 30) {
        const ring: Vec3[] = [];
        for (let lng = -180; lng <= 180; lng += 6) ring.push(latLngToVec(lat, lng));
        arc(ring);
      }
      for (let lng = -180; lng < 180; lng += 30) {
        const ring: Vec3[] = [];
        for (let lat = -85; lat <= 85; lat += 4) ring.push(latLngToVec(lat, lng));
        arc(ring);
      }
    }

    /** Continents. Front hemisphere only — an orthographic dot field with the
     * back half drawn is just twice the dots with none of the depth. */
    function drawLand(): void {
      for (const v of landVecs) {
        const p = project(v);
        if (p.z < 0) continue;
        const fade = 0.7 + 0.3 * p.z;
        ctx!.fillStyle = alpha(palette.dotFront, fade);
        ctx!.beginPath();
        ctx!.arc(p.x, p.y, 1.7 + p.z * 0.7, 0, Math.PI * 2);
        ctx!.fill();
      }
    }

    function drawBursts(dt: number): void {
      for (let i = bursts.length - 1; i >= 0; i -= 1) {
        const b = bursts[i]!;
        b.age += dt;
        if (b.age >= b.ttl) {
          bursts.splice(i, 1);
          continue;
        }
        if (b.age < 0) continue; // the delayed second ring, not started yet
        const p = project(b.vec);
        if (p.z < -0.05) continue;
        const t = b.age / b.ttl;
        const ease = 1 - Math.pow(1 - t, 3);
        const visible = Math.max(0.3, p.z);
        const r = (8 + 26 * ease) * visible;
        ctx!.strokeStyle = palette.ripple((1 - t) * 0.85 * visible);
        ctx!.lineWidth = 1.4 * (1 - t) + 0.4;
        ctx!.beginPath();
        ctx!.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx!.stroke();
        if (t < 0.35) {
          ctx!.fillStyle = palette.ripple((1 - t / 0.35) * 0.14 * visible);
          ctx!.beginPath();
          ctx!.arc(p.x, p.y, r * 0.7, 0, Math.PI * 2);
          ctx!.fill();
        }
      }
    }

    function drawPackets(dt: number, now: number): void {
      for (let i = packets.length - 1; i >= 0; i -= 1) {
        const pkt = packets[i]!;
        pkt.t += pkt.speed * dt;
        if (pkt.t >= 1) {
          bursts.push({ vec: nodeVecs[pkt.to]!, age: 0, ttl: 1100 + Math.random() * 600 });
          packets.splice(i, 1);
          continue;
        }
        if (pkt.t > 0.05) pkt.bursting = false;
        const a = nodeVecs[pkt.from]!;
        const b = nodeVecs[pkt.to]!;

        const trailSteps = 16;
        for (let s = trailSteps; s >= 0; s -= 1) {
          const t = pkt.t - s * 0.011;
          if (t < 0 || t > 1) continue;
          const v = slerp(a, b, t);
          // Lift the path off the surface at mid-flight so an arc reads as an
          // arc rather than as a dot sliding across the map.
          const lift = 1 + 0.18 * Math.sin(t * Math.PI);
          const p = project([v[0] * lift, v[1] * lift, v[2] * lift]);
          if (p.z < -0.1) continue;
          const front = Math.max(0, p.z);
          if (s === 0) {
            const pulse = 0.85 + 0.15 * Math.sin(now * 0.005 + pkt.t * 9);
            const flare = pkt.bursting ? 1.7 : 1;
            ctx!.fillStyle = alpha(palette.packet, 0.25 * front);
            ctx!.beginPath();
            ctx!.arc(p.x, p.y, 6 * pulse * flare, 0, Math.PI * 2);
            ctx!.fill();
            ctx!.fillStyle = palette.packet;
            ctx!.shadowColor = palette.packetGlow;
            ctx!.shadowBlur = 22 * pulse * flare;
            ctx!.beginPath();
            ctx!.arc(p.x, p.y, 3 * pulse * flare, 0, Math.PI * 2);
            ctx!.fill();
            ctx!.shadowBlur = 0;
            ctx!.fillStyle = alpha(palette.sphereInner, 0.95);
            ctx!.beginPath();
            ctx!.arc(p.x, p.y, 1.2 * pulse, 0, Math.PI * 2);
            ctx!.fill();
          } else {
            ctx!.fillStyle = alpha(
              palette.packet,
              (1 - s / trailSteps) * 0.75 * (0.5 + 0.5 * front),
            );
            ctx!.beginPath();
            ctx!.arc(p.x, p.y, 2 - s * 0.09, 0, Math.PI * 2);
            ctx!.fill();
          }
        }
      }
    }

    function drawNodes(now: number): void {
      for (let i = 0; i < nodeVecs.length; i += 1) {
        const p = project(nodeVecs[i]!);
        if (p.z < -0.05) continue;
        const pulse = 0.93 + 0.07 * Math.sin(now * 0.0018 + i * 0.9);
        const visible = 0.55 + 0.45 * Math.max(0, p.z);
        ctx!.fillStyle = palette.nodeHalo(0.2 * visible);
        ctx!.beginPath();
        ctx!.arc(p.x, p.y, 9 * pulse, 0, Math.PI * 2);
        ctx!.fill();
        ctx!.fillStyle = palette.nodeCore(0.98 * visible);
        ctx!.beginPath();
        ctx!.arc(p.x, p.y, 4.6 * pulse, 0, Math.PI * 2);
        ctx!.fill();
      }
    }

    function paint(now: number, dt: number): void {
      ctx!.clearRect(0, 0, cssW, cssH);
      drawSphere();
      drawGraticule();
      drawLand();
      drawBursts(dt);
      drawPackets(dt, now);
      drawNodes(now);
    }

    function frame(now: number): void {
      if (disposed) return;
      const dt = Math.min(40, now - lastFrame);
      lastFrame = now;

      const introT = Math.min(1, (now - introStart) / INTRO_MS);
      const settle = 1 + Math.pow(1 - introT, 4) * (INTRO_MULT - 1);
      yaw += YAW_VEL * dt * settle;

      nextSpawnIn -= dt;
      if (nextSpawnIn <= 0 && packets.length < 36) {
        spawnPacket();
        nextSpawnIn = 250 + Math.random() * 450;
      }

      paint(now, dt);
      raf = requestAnimationFrame(frame);
    }

    /** One still frame — what a reduced-motion visitor gets, and what a
     * resize repaints while the loop is suspended. */
    function still(): void {
      paint(performance.now(), 0);
    }

    const onResize = () => {
      if (disposed) return;
      resize();
      if (reduceMotion) still();
    };

    resize();
    globalThis.addEventListener("resize", onResize);

    if (reduceMotion) still();
    else raf = requestAnimationFrame(frame);

    void loadLandRings().then((rings) => {
      if (disposed) return;
      landVecs = sampleLandDots(prepareLandMask(rings)).map((d: LandDot) =>
        latLngToVec(d.lat, d.lng),
      );
      if (reduceMotion) still();
    });

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      globalThis.removeEventListener("resize", onResize);
    };
  }, [palette]);

  return (
    <Box
      className={className}
      aria-hidden
      sx={{ position: "relative", width: "100%", height: "100%", pointerEvents: "none" }}
    >
      <Box
        component="canvas"
        ref={canvasRef}
        data-testid="login-globe"
        sx={{ display: "block", width: "100%", height: "100%" }}
      />
    </Box>
  );
}
